import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconEdit,
  IconFolder,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import type {
  FileOutput,
  FolderOutput,
  TrainerSessionTypeOutput,
} from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { FileUploadModal } from "@/components/FileUploadModal";
import { FolderNavigator } from "@/components/FolderNavigator";
import { Header } from "@/components/Header";
import { LibraryItem } from "@/components/LibraryItem";
import { TrainerRoutes, useNavigator } from "@/routers/routes";
import { specialtyOptions, getSpecialtyLabel } from "./common";
import { alwaysArray } from "@/utils/stuff";

type SessionType = TrainerSessionTypeOutput;

export const SessionTypes = () => {
  const { data: sessionTypes } = client.useQuery(
    "get",
    "/trainer/profile/session-types",
    undefined,
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  if (!sessionTypes) {
    return <FancyLoader />;
  }

  return <SessionTypesList sessionTypes={sessionTypes} />;
};

const SessionTypesList = (props: { sessionTypes: SessionType[] }) => {
  const { navigate } = useNavigator();

  const { data: schedules } = client.useQuery(
    "get",
    "/trainer/profile/schedules",
    undefined,
    { staleTime: 0, gcTime: 0 },
  );

  const handleEdit = (sessionTypeId: string) => {
    navigate({
      path: TrainerRoutes.PROFILE.SESSION_TYPE_EDIT,
      params: { sessionTypeId },
    });
  };

  const handleAdd = () => {
    navigate({
      path: TrainerRoutes.PROFILE.SESSION_TYPE_EDIT,
      params: { sessionTypeId: "new" },
    });
  };

  const getScheduleName = (scheduleId: string) => {
    const schedule = schedules?.find((s) => s.id === scheduleId);
    return schedule?.name || "Unknown Schedule";
  };

  if (!schedules) {
    return <FancyLoader />;
  }

  return (
    <Stack gap="xs">
      <Header title="Session Types" />
      <Stack gap="md">
        {props.sessionTypes.map((sessionType) => (
          <Card key={sessionType.id} withBorder shadow="sm" radius="md">
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xl" fw={600}>
                    {sessionType.name}
                  </Text>
                  {sessionType.description && (
                    <Text>{sessionType.description}</Text>
                  )}
                </Stack>
                <Button
                  variant="outline"
                  onClick={() => handleEdit(sessionType.id)}
                  leftSection={<IconEdit size="1.2em" />}
                >
                  Edit
                </Button>
              </Group>

              <Group gap="md">
                <Text>{sessionType.durationMinutes} minutes</Text>
                <Badge size="lg" variant="filled">
                  {sessionType.online ? "Virtual" : "In-person"}
                </Badge>
                <Badge
                  size="lg"
                  variant="filled"
                  color={sessionType.bookable ? "third.5" : "red.6"}
                  leftSection={
                    sessionType.bookable ? (
                      <IconCheck size="1rem" />
                    ) : (
                      <IconX size="1rem" />
                    )
                  }
                >
                  {sessionType.bookable ? "Available" : "Disabled"}
                </Badge>
                {sessionType.scheduleId && (
                  <Badge size="lg" variant="light" color="blue">
                    {getScheduleName(sessionType.scheduleId)}
                  </Badge>
                )}
              </Group>
              {alwaysArray(sessionType.themes).length > 0 && (
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    Themes:
                  </Text>
                  {alwaysArray(sessionType.themes).map((theme) => (
                    <Badge key={theme} size="sm" variant="light" color="green">
                      {getSpecialtyLabel(theme)}
                    </Badge>
                  ))}
                </Group>
              )}
            </Stack>
          </Card>
        ))}

        {!props.sessionTypes.some((sessionType) => sessionType.bookable) && (
          <Alert
            title="Consider making at least one session type available for booking. Otherwise clients will not be able to book any sessions with you."
            color="yellow"
          />
        )}

        <Button
          variant="outline"
          fullWidth
          onClick={handleAdd}
          leftSection={<IconPlus size="1.2em" />}
          size="md"
        >
          Add Session Type
        </Button>
      </Stack>
    </Stack>
  );
};

export const SessionTypeEdit = () => {
  const { sessionTypeId } = useParams();
  const { navigate } = useNavigator();
  const isNew = sessionTypeId === "new";

  const { data: sessionTypes } = client.useQuery(
    "get",
    "/trainer/profile/session-types",
    undefined,
    { staleTime: 0, gcTime: 0 },
  );

  const { data: libraryFiles, refetch: refreshLibraryFiles } = client.useQuery(
    "get",
    "/universal/files/",
    {},
    { staleTime: 0, gcTime: 0 },
  );

  const { data: schedules } = client.useQuery(
    "get",
    "/trainer/profile/schedules",
    undefined,
    { staleTime: 0, gcTime: 0 },
  );

  const updateMutation = client.useMutation(
    "post",
    "/trainer/profile/session-types",
  );

  if (!sessionTypes || !libraryFiles || !schedules) {
    return <FancyLoader />;
  }

  const handleSubmit = async (values: SessionType) => {
    const updatedSessionTypes = sessionTypes ? [...sessionTypes] : [];
    const idx = updatedSessionTypes.findIndex((st) => st.id === values.id);

    const sessionType = {
      ...values,
      price: Math.round(values.price * 100),
    };

    if (idx === -1) {
      updatedSessionTypes.push(sessionType);
    } else {
      updatedSessionTypes[idx] = sessionType;
    }

    await updateMutation.mutateAsync({ body: updatedSessionTypes });
    navigate({ path: TrainerRoutes.PROFILE.SESSION_TYPES });
  };

  const handleDelete = async () => {
    if (!sessionTypes) return;
    const updated = sessionTypes.filter((st) => st.id !== sessionTypeId);
    await updateMutation.mutateAsync({ body: updated });
    navigate({ path: TrainerRoutes.PROFILE.SESSION_TYPES });
  };

  const existingSessionType = sessionTypes.find(
    (st) => st.id === sessionTypeId,
  );

  // Get the default schedule ID (first schedule or empty string)
  const defaultScheduleId =
    schedules && schedules.length > 0 ? schedules[0]?.id || "" : "";

  const initialValues: SessionType = existingSessionType
    ? {
        ...existingSessionType,
        price: existingSessionType.price / 100,
        included: existingSessionType.included || [],
        scheduleId: existingSessionType.scheduleId || defaultScheduleId,
        themes: existingSessionType.themes || [],
      }
    : {
        id: crypto.randomUUID().replace(/-/g, ""),
        name: "",
        description: "",
        preparation: "",
        durationMinutes: 60,
        online: false,
        bookable: true,
        price: 0,
        included: [],
        scheduleId: defaultScheduleId,
        themes: [],
      };

  return (
    <SessionTypeEditForm
      isNew={isNew}
      initialValues={initialValues}
      libraryFiles={libraryFiles}
      schedules={schedules}
      refreshLibraryFiles={refreshLibraryFiles}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      isLoading={updateMutation.isPending}
    />
  );
};

// --- SessionTypeEditForm.tsx ---

type Props = {
  isNew: boolean;
  initialValues: SessionType;
  libraryFiles?: { files: FileOutput[]; folders: FolderOutput[] };
  schedules: Array<{ id: string; name: string }>;
  refreshLibraryFiles: () => void;
  onSubmit: (values: SessionType) => Promise<void>;
  onDelete: () => Promise<void>;
  isLoading?: boolean;
};

const SessionTypeEditForm = ({
  isNew,
  initialValues,
  libraryFiles,
  schedules,
  refreshLibraryFiles,
  onSubmit,
  onDelete,
  isLoading = false,
}: Props) => {
  const form = useForm<SessionType>({
    name: "session-type-form",
    initialValues,
    validate: {
      name: isNotEmpty("Name is required"),
      durationMinutes: isNotEmpty("Duration is required"),
      scheduleId: isNotEmpty("Schedule is required"),
      price: (value) =>
        value < 0 || Number.isNaN(value)
          ? "Price must be a positive number"
          : null,
    },
  });

  const [selectedFileIds, setSelectedFileIds] = useState<string[]>(
    initialValues.fileIds ?? [],
  );
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>(
    initialValues.folderIds ?? [],
  );
  const [deleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  const handleSubmit = (values: SessionType) => {
    onSubmit({
      ...values,
      fileIds: selectedFileIds,
      folderIds: selectedFolderIds,
    });
  };

  const handleFileSelect = (file: FileOutput) => {
    if (!selectedFileIds.includes(file.id)) {
      setSelectedFileIds((prev) => [...prev, file.id]);
    }
  };

  const handleFolderSelect = (folder: FolderOutput) => {
    if (!selectedFolderIds.includes(folder.id)) {
      setSelectedFolderIds((prev) => [...prev, folder.id]);
    }
  };

  const handleFileRemove = (fileId: string) => {
    setSelectedFileIds((prev) => prev.filter((id) => id !== fileId));
  };

  const handleFolderRemove = (folderId: string) => {
    setSelectedFolderIds((prev) => prev.filter((id) => id !== folderId));
  };

  const onFileUpload = (file: FileOutput) => {
    handleFileSelect(file);
    refreshLibraryFiles();
  };

  const selectedFiles = useMemo(
    () =>
      libraryFiles?.files.filter((file) => selectedFileIds.includes(file.id)) ??
      [],
    [libraryFiles, selectedFileIds],
  );

  const selectedFolders = useMemo(
    () =>
      libraryFiles?.folders.filter((folder) =>
        selectedFolderIds.includes(folder.id),
      ) ?? [],
    [libraryFiles, selectedFolderIds],
  );

  return (
    <Stack>
      <Header
        title={isNew ? "New Session Type" : "Edit Session Type"}
        actionControl={
          <Button
            leftSection={<IconTrash size={16} />}
            variant="outline"
            onClick={openDeleteModal}
          >
            Delete
          </Button>
        }
      />
      <form name="session-type-form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g. Basic Training"
            {...form.getInputProps("name")}
          />

          <TextInput
            label="Description (optional)"
            placeholder="Describe what this session includes"
            {...form.getInputProps("description")}
          />

          <Select
            label="Schedule"
            placeholder="Select a schedule for this session type"
            data={schedules.map((schedule) => ({
              value: schedule.id,
              label: schedule.name,
            }))}
            {...form.getInputProps("scheduleId")}
          />

          <MultiSelect
            label="Session themes (optional)"
            placeholder="Select up to 3 themes"
            data={specialtyOptions}
            maxValues={3}
            {...form.getInputProps("themes")}
            description="This will help clients find your services. Up to 3 themes."
            searchable
          />

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Duration (minutes)
            </Text>
            <SegmentedControl
              style={{ backgroundColor: "white" }}
              color="blue"
              value={form.values.durationMinutes.toString()}
              onChange={(value) =>
                form.setFieldValue("durationMinutes", parseInt(value))
              }
              data={Array.from({ length: 7 }, (_, i) => (i + 2) * 15).map(
                (duration) => ({
                  label: `${duration}`,
                  value: duration.toString(),
                }),
              )}
              fullWidth
            />
          </Stack>

          <Checkbox
            label="Available for booking"
            {...form.getInputProps("bookable", { type: "checkbox" })}
          />
          <Checkbox
            label="Online session"
            {...form.getInputProps("online", { type: "checkbox" })}
          />

          <NumberInput
            label="Price (€)"
            {...form.getInputProps("price")}
            decimalScale={2}
            fixedDecimalScale
            decimalSeparator=","
            min={0}
            step={0.01}
            required={true}
            error={form.errors.price}
            hideControls
          />

          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={500}>
                What's included in this package? (optional)
              </Text>
              <ActionIcon
                variant="light"
                size="sm"
                onClick={() => {
                  form.insertListItem("included", "");
                }}
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Group>
            <Stack gap="xs">
              {(form.values.included || []).map((_, index) => (
                <Group key={index} align="flex-start">
                  <TextInput
                    placeholder="Enter what's included..."
                    style={{ flex: 1 }}
                    key={form.key(`included.${index}`)}
                    {...form.getInputProps(`included.${index}`)}
                  />
                  <ActionIcon
                    color="red"
                    variant="light"
                    onClick={() => {
                      form.removeListItem("included", index);
                    }}
                  >
                    <IconTrash size="1rem" />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
            {(form.values.included || []).length === 0 && (
              <Text size="xs" c="dimmed">
                Add items that are included in this session package
              </Text>
            )}
          </Stack>

          <Divider
            label="Information below is only visible only after booking"
            labelPosition="center"
          />

          <Textarea
            label="Preparation Instructions (optional)"
            placeholder="What should clients do to prepare for this session?"
            autosize
            minRows={3}
            {...form.getInputProps("preparation")}
          />

          <Stack gap="md">
            <Text fw={500}>Files to share</Text>
            <Group>
              <FolderNavigator
                onFileSelect={handleFileSelect}
                onFolderSelect={handleFolderSelect}
              >
                <Button
                  variant="outline"
                  leftSection={<IconFolder size="1.2em" />}
                  style={{ flex: 1 }}
                >
                  Pick from Library
                </Button>
              </FolderNavigator>
              <FileUploadModal onSuccess={onFileUpload} />
            </Group>

            <Stack gap="md">
              {selectedFolders.map((folder) => (
                <LibraryItem
                  key={folder.id}
                  kind="folder"
                  item={folder}
                  readonly={false}
                  onDelete={() => handleFolderRemove(folder.id)}
                  onFolderClick={() => {}}
                />
              ))}

              {selectedFiles.map((file) => (
                <LibraryItem
                  key={file.id}
                  kind="file"
                  item={file}
                  readonly={false}
                  onDelete={() => handleFileRemove(file.id)}
                />
              ))}
            </Stack>
          </Stack>

          <Button type="submit" fullWidth loading={isLoading}>
            Save Changes
          </Button>
        </Stack>
        <Modal
          opened={deleteModalOpen}
          onClose={closeDeleteModal}
          title="Delete Session Type"
        >
          <Stack>
            <Text>
              Are you sure you want to delete this session type? This action
              cannot be undone.
            </Text>
            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={closeDeleteModal}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button color="red" onClick={onDelete} loading={isLoading}>
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </form>
    </Stack>
  );
};
